<template>
  <div :class="computedCardClasses">
    <!-- Label avec icône OBLIGATOIRE pour accessibilité -->
    <div class="tw-mb-2 tw-flex tw-items-center tw-gap-2">
      <slot name="label">
        <label 
          :for="controlId"
          :class="computedLabelClasses"
          :id="controlId + '-label'">
          <!-- Icône obligatoire -->
          <i :class="displayIcon" aria-hidden="true"></i>
          {{ displayLabel }}
          <abbr v-if="mandatory" title="obligatoire" class="tw-text-red-600 tw-ml-1 tw-no-underline">*</abbr>
        </label>
      </slot>
    </div>
    
    <!-- Contenu principal avec autocomplete automatique -->
    <div class="tw-flex-1">
      <slot :autocomplete="autocomplete" :controlId="controlId" :mandatory="mandatory"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppCard',
  props: {
    // Contenu
    label: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    
    // Apparence
    variant: {
      type: String,
      default: 'light',
      validator: value => ['light', 'primary', 'secondary', 'white', 'transparent'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg'].includes(value)
    },
    
    // Comportement
    mandatory: {
      type: Boolean,
      default: false
    },
    
    // Accessibilité - OBLIGATOIRE
    controlId: {
      type: String,
      required: true
    },
    
    // Autocomplete pour WCAG 2.2 AA 1.3.5
    autocomplete: {
      type: String,
      required: true,
      validator: value => [
        'off', 'on', 'name', 'email', 'username', 'new-password', 'current-password',
        'organization', 'country', 'bday', 'tel', 'url', 'given-name', 'family-name',
        'street-address', 'postal-code', 'cc-name', 'cc-number', 'cc-exp',
        'transaction-currency', 'transaction-amount'
      ].includes(value)
    },
    
    // Classes CSS supplémentaires
    customClass: {
      type: String,
      default: ''
    },
    
    // Layout responsive automatique
    responsiveLayout: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    computedCardClasses() {
      const classes = ['app-card-base']
      
      // Layout responsive
      if (this.responsiveLayout) {
        classes.push('tw-w-full')
      }
      
      // Variantes de style avec classes CSS personnalisées
      if (this.variant === 'light') {
        classes.push('app-card-light')
      } else if (this.variant === 'primary') {
        classes.push('app-card-primary')
      } else if (this.variant === 'secondary') {
        classes.push('app-card-secondary')
      } else if (this.variant === 'white') {
        classes.push('app-card-white')
      } else if (this.variant === 'transparent') {
        classes.push('app-card-transparent')
      }
      
      // Taille
      if (this.size === 'lg') {
        classes.push('app-card-lg')
      } else if (this.size === 'sm') {
        classes.push('app-card-sm')
      } else {
        classes.push('app-card-md')
      }
      
      // Classes personnalisées
      if (this.customClass) {
        classes.push(this.customClass)
      }
      
      return classes.join(' ')
    },
    computedLabelClasses() {
      // UNIFORMISATION : texte noir normal (pas en gras) avec icône obligatoire
      const classes = ['tw-font-normal tw-text-black tw-flex tw-items-center tw-gap-2']
      
      // Taille du texte selon la taille de la carte
      if (this.size === 'lg') {
        classes.push('tw-text-base')
      } else if (this.size === 'sm') {
        classes.push('tw-text-xs')
      } else {
        classes.push('tw-text-sm')
      }
      
      return classes.join(' ')
    },
    displayLabel() {
      return this.label
    },
    displayIcon() {
      return this.icon
    }
  }
}
</script>

<style scoped>
/* Styles de base pour les cartes AppCard - AGRESSIF comme AppButton */
.app-card-base {
  display: block !important;
  width: 100% !important;
  transition: all 0.2s ease !important;
}

/* Tailles */
.app-card-sm {
  padding: 0.5rem !important;
}

.app-card-md {
  padding: 0.75rem !important;
}

.app-card-lg {
  padding: 1rem !important;
}

/* Variantes avec fond et bordure très visibles */
.app-card-light {
  background-color: #f8f9fa !important;
  border: 2px solid #dee2e6 !important;
  border-radius: 0.5rem !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.app-card-light:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.app-card-primary {
  background-color: #f8f9fa !important;
  border: 2px solid #0a6060 !important;
  border-radius: 0.5rem !important;
  box-shadow: 0 4px 8px rgba(10, 96, 96, 0.2) !important;
}

.app-card-primary:hover {
  background-color: #e9ecef !important;
  border-color: #084d4d !important;
  box-shadow: 0 6px 12px rgba(10, 96, 96, 0.3) !important;
}

.app-card-secondary {
  background-color: #f8f9fa !important;
  border: 2px solid #6c757d !important;
  border-radius: 0.5rem !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.app-card-secondary:hover {
  background-color: #e9ecef !important;
  border-color: #5a6268 !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.app-card-white {
  background-color: #ffffff !important;
  border: 2px solid #dee2e6 !important;
  border-radius: 0.5rem !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

.app-card-white:hover {
  background-color: #f8f9fa !important;
  border-color: #adb5bd !important;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.app-card-transparent {
  background-color: #f8f9fa !important;
  border: 1px solid #dee2e6 !important;
  border-radius: 0.5rem !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.app-card-transparent:hover {
  background-color: #e9ecef !important;
  border-color: #adb5bd !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
}

/* Focus ring pour l'accessibilité */
:deep(input:focus),
:deep(select:focus),
:deep(textarea:focus),
:deep(.form-control:focus) {
  @apply tw-ring-2 tw-ring-teal-500 tw-ring-offset-2 tw-border-teal-500;
}

/* Styles pour les éléments de formulaire dans la carte */
:deep(.form-control) {
  @apply tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm;
  @apply focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal-500 focus:tw-border-teal-500;
  @apply tw-transition-colors tw-duration-200;
}

:deep(.form-control:disabled) {
  @apply tw-bg-gray-100 tw-text-gray-500 tw-cursor-not-allowed;
}

/* Responsive breakpoints personnalisés si nécessaire */
@media (min-width: 768px) {
  .responsive-card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }
}

/* Support pour les dropdowns Bootstrap dans les cartes */
:deep(.dropdown) {
  @apply tw-w-full;
}

:deep(.dropdown-toggle) {
  @apply tw-w-full tw-text-left;
}

/* Amélioration de l'accessibilité */
:deep([aria-invalid="true"]) {
  @apply tw-border-red-500 tw-ring-red-500;
}

/* Indicateur de champ obligatoire */
.mandatory-indicator {
  @apply tw-text-red-600 tw-font-semibold;
}
</style>